import React from 'react';
import { useState } from 'react';
import { IoChevronBack, IoChevronForward, IoPersonOutline, IoThumbsUp, IoRemoveOutline, IoThumbsDown } from 'react-icons/io5';
import { evaluatePoint } from '../../api/MemberApi';
import SelectModal from './SelectModal';
import { AlertModal } from './AlertModal';

const EvaluationModal = ({ isVisible, onClose, onComplete, members, meetingId, currentUserId }) => {
    const [currentMemberIndex, setCurrentMemberIndex] = useState(0);
    const [evaluations, setEvaluations] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    // 모달 상태 관리
    const [alertState, setAlertState] = useState({ isVisible: false, message: '', callback: null });

    const getErrorMessage = (error) => {
        if (error.response && error.response.data && error.response.data.message) {
            return error.response.data.message;
        }
        return '오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
    };

    const evaluatableMembers = members.filter(member => member.id !== currentUserId);

    if (evaluatableMembers.length === 0) {
        return (
            <SelectModal
                isVisible={isVisible}
                onClose={onClose}
                title="평가할 멤버가 없습니다"
                confirmText="닫기"
                showButtons={false}
            >
                <div className="text-center">
                    <button
                        onClick={onClose}
                        className="w-full py-3 bg-gray-500 text-white rounded-lg"
                    >
                        닫기
                    </button>
                </div>
            </SelectModal>
        );
    }

    const currentMember = evaluatableMembers[currentMemberIndex];

    const handleEvaluation = (type) => {
        setEvaluations(prev => ({
            ...prev,
            [currentMember.id]: type
        }));
    };

    const handleNext = () => {
        if (currentMemberIndex < evaluatableMembers.length - 1) {
            setCurrentMemberIndex(prev => prev + 1);
        }
    };

    const handlePrev = () => {
        if (currentMemberIndex > 0) {
            setCurrentMemberIndex(prev => prev - 1);
        }
    };

    const handleSubmit = async () => {
        const allEvaluated = evaluatableMembers.every(member =>
            evaluations[member.id] !== undefined
        );

        if (!allEvaluated) {
            setAlertState({ isVisible: true, message: '모든 멤버에 대한 평가를 완료해주세요.' });
            return;
        }

        try {
            setIsLoading(true);
            const evaluatePointDtoList = evaluatableMembers.map(member => ({
                targetMemberId: member.id,
                meetingId: meetingId,
                type: evaluations[member.id]
            }));

            await evaluatePoint({
                meetingId: meetingId,
                evaluatePointDtoList: evaluatePointDtoList
            });

            setAlertState({
                isVisible: true,
                message: '평가가 완료되었습니다!',
                callback: onComplete || onClose
            });
        } catch (error) {
            console.error('평가 제출 실패:', error);
            setAlertState({ isVisible: true, message: getErrorMessage(error) });
        } finally {
            setIsLoading(false);
        }
    };

    const allEvaluated = evaluatableMembers.every(member =>
        evaluations[member.id] !== undefined
    );

    return (
        <SelectModal
            isVisible={isVisible}
            onClose={onClose}
            title="밥친구 평가"
            onConfirm={handleSubmit}
            confirmText="평가 완료"
            confirmDisabled={!allEvaluated}
            isLoading={isLoading}
        >
            <AlertModal
                isVisible={alertState.isVisible}
                message={alertState.message}
                callback={alertState.callback}
                onClose={() => setAlertState({ isVisible: false, message: '', callback: null })}
            />
            {/* 진행 상황 */}
            <div className="text-center mb-4">
                <span className="text-sm text-gray-500">
                    {currentMemberIndex + 1} / {evaluatableMembers.length}
                </span>
            </div>

            {/* 멤버 정보 */}
            <div className="flex flex-col items-center mb-6">
                <div className="w-20 h-20 rounded-full bg-gray-200 overflow-hidden mb-3 flex items-center justify-center">
                    {currentMember.imageUrl ? (
                        <img
                            src={currentMember.imageUrl}
                            alt={currentMember.name}
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <IoPersonOutline size={32} className="text-gray-400" />
                    )}
                </div>
                <h4 className="text-lg font-medium text-gray-900">{currentMember.name}</h4>
                <p className="text-sm text-gray-500">이 밥친구는 어떠셨나요?</p>
            </div>

            {/* 평가 버튼들 */}
            <div className="flex space-x-3 mb-6">
                <button
                    onClick={() => handleEvaluation('NEGATIVE')}
                    className={`flex-1 py-4 px-3 rounded-lg font-medium transition-colors flex flex-col items-center justify-center space-y-1 ${
                        evaluations[currentMember.id] === 'NEGATIVE'
                            ? 'bg-blue-500 text-white'
                            : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                    }`}
                >
                    <IoThumbsDown size={24} />
                    <span className="text-sm">찬 밥</span>
                </button>
                <button
                    onClick={() => handleEvaluation('NONE')}
                    className={`flex-1 py-4 px-3 rounded-lg font-medium transition-colors flex flex-col items-center justify-center space-y-1 ${
                        evaluations[currentMember.id] === 'NONE'
                            ? 'bg-gray-500 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                    <IoRemoveOutline size={24} />
                    <span className="text-sm">미지근한 밥</span>
                </button>
                <button
                    onClick={() => handleEvaluation('POSITIVE')}
                    className={`flex-1 py-4 px-3 rounded-lg font-medium transition-colors flex flex-col items-center justify-center space-y-1 ${
                        evaluations[currentMember.id] === 'POSITIVE'
                            ? 'bg-red-500 text-white'
                            : 'bg-red-100 text-red-600 hover:bg-red-200'
                    }`}
                >
                    <IoThumbsUp size={24} />
                    <span className="text-sm">더운 밥</span>
                </button>
            </div>

            {/* 네비게이션 버튼들 */}
            <div className="flex justify-between items-center">
                <button
                    onClick={handlePrev}
                    disabled={currentMemberIndex === 0}
                    className={`p-2 rounded-full ${
                        currentMemberIndex === 0
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-600 hover:bg-gray-100'
                    }`}
                >
                    <IoChevronBack size={24} />
                </button>

                <div className="flex space-x-1">
                    {evaluatableMembers.map((_, index) => (
                        <div
                            key={index}
                            className={`w-2 h-2 rounded-full ${
                                index === currentMemberIndex
                                    ? 'bg-amber-500'
                                    : evaluations[evaluatableMembers[index].id]
                                        ? 'bg-green-500'
                                        : 'bg-gray-300'
                            }`}
                        />
                    ))}
                </div>

                <button
                    onClick={handleNext}
                    disabled={currentMemberIndex === evaluatableMembers.length - 1}
                    className={`p-2 rounded-full ${
                        currentMemberIndex === evaluatableMembers.length - 1
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-600 hover:bg-gray-100'
                    }`}
                >
                    <IoChevronForward size={24} />
                </button>
            </div>
        </SelectModal>
    );
};

export default EvaluationModal;
